import type { CloudConfig, CloudConnection, Manual, RemoteRef, RuntimeStatus } from "./types";

const defaultOrigin = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:8002"
  : "https://allmanual1.jchulsbio.com";

export const API_ORIGIN = import.meta.env.VITE_LOCAL_API_ORIGIN || defaultOrigin;

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getHealth(): Promise<RuntimeStatus> {
  return parseJson<RuntimeStatus>(await fetch(`${API_ORIGIN}/api/health`));
}

export async function getManuals(ownerId?: string): Promise<Manual[]> {
  const url = new URL(`${API_ORIGIN}/api/manuals`);
  if (ownerId) url.searchParams.set("ownerId", ownerId);
  return parseJson<Manual[]>(await fetch(url));
}

export async function searchManuals(query: string, ownerId?: string): Promise<Manual[]> {
  const url = new URL(`${API_ORIGIN}/api/search`);
  url.searchParams.set("q", query);
  if (ownerId) url.searchParams.set("ownerId", ownerId);
  return parseJson<Manual[]>(await fetch(url));
}

export async function uploadManual(form: FormData): Promise<Manual> {
  return parseJson<Manual>(
    await fetch(`${API_ORIGIN}/api/manuals`, {
      method: "POST",
      body: form
    })
  );
}

export async function processManual(manualId: string): Promise<Manual> {
  return parseJson<Manual>(
    await fetch(`${API_ORIGIN}/api/process`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ manualId })
    })
  );
}

export async function getCloudConnections(): Promise<CloudConnection[]> {
  return parseJson<CloudConnection[]>(await fetch(`${API_ORIGIN}/api/cloud/connections`));
}

export async function getCloudConfig(): Promise<CloudConfig> {
  return parseJson<CloudConfig>(await fetch(`${API_ORIGIN}/api/cloud/config`));
}

export async function connectCloud(provider: "google" | "apple", rootPath: string): Promise<CloudConnection> {
  return parseJson<CloudConnection>(
    await fetch(`${API_ORIGIN}/api/cloud/${provider}/connect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rootPath })
    })
  );
}

export async function saveSyncNote(manualId: string, ref: RemoteRef): Promise<Manual> {
  return parseJson<Manual>(
    await fetch(`${API_ORIGIN}/api/manuals/${manualId}/sync-note`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...ref,
        status: "synced"
      })
    })
  );
}

export async function importManuals(manuals: Manual[], ownerId: string): Promise<{ imported: Manual[] }> {
  return parseJson<{ imported: Manual[] }>(
    await fetch(`${API_ORIGIN}/api/manuals/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ manuals, ownerId })
    })
  );
}
